import TokenizedArray from './TokenizedArray';
import Reporter from './Reporter';

type CleanupFunction = () => void;

export type Listener<T extends Array<unknown>> = (...args: T) => unknown;

export default class Signal<T extends Array<unknown> = []> {
    private debugDispatchProgress: Array<() => number> = [];
    public readonly reporter = new Reporter();

    private listeners = new TokenizedArray<Listener<T>>();

    public addListenerOnce(listener: Listener<T>): CleanupFunction {
        let cleanup: CleanupFunction | null = null;

        cleanup = this.addListener((...args: T) => {
            if (cleanup) {
                cleanup();
                cleanup = null;
            }

            listener(...args);
        });

        return cleanup;
    }

    public addListener(listener: Listener<T>): CleanupFunction {
        this.debugWarnIfDispatching('Adding listener during dispatch - it will be ignored');

        const indexToken = this.listeners.add(listener);

        return () => {
            const index = this.listeners.tokens.indexOf(indexToken);
            if (index !== -1) {
                if (this.debugDispatchProgress.some(getI => getI() < index)) {
                    this.debugWarnIfDispatching('Removing pending listener during dispatch - will be executed anyway');
                }
                this.listeners.safeRemoveAtIndex(index);
            }
        };
    }

    public removeAllListeners(): void {
        if (this.debugDispatchProgress.some(getI => getI() < this.listeners.getLength() - 1)) {
            this.debugWarnIfDispatching('removeAllListeners during dispatch');
        }

        this.listeners.removeAll();
    }

    public dispatch(...args: T): void {
        this.debugWarnIfDispatching('Recursive dispatch');

        let prorgess: number = -1;
        this.debugDispatchProgress.push(() => prorgess);
        try {
            const copy = this.listeners.elements.slice();
            copy.forEach((listener, i) => {
                prorgess = i;
                listener(...args);
            });
        } finally {
            this.debugDispatchProgress.pop();
        }
    }

    //

    private debugWarnIfDispatching(message: string): void {
        if (this.debugDispatchProgress.length !== 0) {
            this.reporter.report(message, { once: true });
        }
    }
}
